import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import wordmark from "../../assets/brand/wordmark.svg";
import { NAV_SECTIONS, SITE } from "../../shared/config/site";
import { langFromPath, localizedPath } from "../../shared/i18n/lang";
import { GOALS, reachGoal } from "../../shared/lib/metrika";
import { formatSupportPhone, usePublicSettings } from "../../shared/lib/usePublicSettings";
import { Icon, LangSwitch, SectionLink, StoreBtn } from "../../shared/ui";
import "./footer.css";

// Module-level: the site is prerendered on every deploy, so SSG and the client agree (a render-time
// `new Date()` would only differ across a new-year boundary between build and visit).
const year = new Date().getFullYear();

// Footer (prototype 355–375). `minimal` (legal pages): brand + the bottom bar only.
const Footer = ({ minimal = false }: { minimal?: boolean }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  const settings = usePublicSettings();

  const logo = (
    <Link to={localizedPath("/", lang)} className="logo" aria-label={SITE.name}>
      <img src={wordmark} alt="Nasiyah" />
    </Link>
  );

  const bottom = (
    <div className="ftr-bot">
      <span>
        © {year} {SITE.name}. {t("footer.footer_copyright")}
      </span>
      <nav>
        <Link to={localizedPath("/business-app/privacy", lang)}>{t("footer.privacy")}</Link>
        <Link to={localizedPath("/business-app/terms", lang)}>{t("footer.terms")}</Link>
        <LangSwitch />
      </nav>
    </div>
  );

  if (minimal) {
    return (
      <footer className="ftr min">
        <div className="wrap">
          <div className="ftr-brand">{logo}</div>
          {bottom}
        </div>
      </footer>
    );
  }

  return (
    <footer className="ftr">
      <div className="wrap">
        <div className="ftr-grid">
          <div className="ftr-brand">
            {logo}
            <p>{t("common.slogan")}</p>
            <div className="soc">
              <a
                href={settings.supportTelegramLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
                onClick={() => reachGoal(GOALS.telegram)}
              >
                <Icon name="g-tg" className="glyph" />
              </a>
              <a
                href={SITE.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Icon name="i-insta" />
              </a>
            </div>
          </div>

          <nav className="ftr-col">
            <h5>{t("footer.product")}</h5>
            {NAV_SECTIONS.map((s) => (
              <SectionLink key={s.id} id={s.id}>
                {t(s.key)}
              </SectionLink>
            ))}
          </nav>

          <div className="ftr-col">
            <h5>{t("footer.contact")}</h5>
            <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
            <a href={`tel:${settings.supportPhone}`}>{formatSupportPhone(settings.supportPhone)}</a>
            <span>{t("footer.address")}</span>
          </div>

          <div className="ftr-col">
            <h5>{t("footer.upload")}</h5>
            <StoreBtn store="apple" tone="secondary" />
            <StoreBtn store="play" tone="secondary" />
          </div>
        </div>
        {bottom}
      </div>
    </footer>
  );
};

export default Footer;
